// src/components/admin/PersonalisationScheduling.jsx
// "Scheduling" section for Portal Personalisation Campaigns. Mirrors the Scheduling section of the
// Email / Push / WhatsApp editors, plus an end date/time (the personalisation is shown from the
// scheduled start until the scheduled end). The "exclude previous recipients" options are omitted
// because nothing is sent — the content simply shows in the portal during the window.
import React from 'react';

const today = () => new Date().toISOString().split('T')[0];

export const initialSchedule = () => ({
  schedulingType: 'one-time', // one-time or recurring
  scheduledDate: '',
  scheduledTime: '',
  scheduledEndDate: '',
  scheduledEndTime: '',
  recurring: {
    frequency: 'daily', // daily, weekly, monthly, annually
    dayOfWeek: 'monday',
    dayOfMonth: 1,
    monthOfYear: 'january',
    dayOfYear: 1,
    startDate: '',
    endDate: '',
    endType: 'date', // date or cycles
    numberOfCycles: 1
  }
});

/** Returns an { fieldName: message } object; empty when the schedule is valid. */
export const validateSchedule = (schedule) => {
  const errors = {};
  const now = new Date();

  if (schedule.schedulingType === 'one-time') {
    if (!schedule.scheduledDate) errors.scheduledDate = 'Scheduled date is required';
    if (!schedule.scheduledTime) errors.scheduledTime = 'Scheduled time is required';
    if (!schedule.scheduledEndDate) errors.scheduledEndDate = 'Scheduled end date is required';
    if (!schedule.scheduledEndTime) errors.scheduledEndTime = 'Scheduled end time is required';

    if (schedule.scheduledDate && schedule.scheduledTime && schedule.scheduledEndDate && schedule.scheduledEndTime) {
      const start = new Date(`${schedule.scheduledDate}T${schedule.scheduledTime}`);
      const end = new Date(`${schedule.scheduledEndDate}T${schedule.scheduledEndTime}`);
      if (end <= now) {
        errors.scheduledEndDateTime = 'Scheduled end date and time must be in the future';
      } else if (end <= start) {
        errors.scheduledEndDateTime = 'Scheduled end must be after the scheduled start';
      }
    }
  } else {
    const r = schedule.recurring;
    if (!schedule.scheduledTime) errors.scheduledTime = 'Start time is required';
    if (!schedule.scheduledEndTime) errors.scheduledEndTime = 'End time is required';
    if (!r.startDate) errors.recurringStartDate = 'Start date is required for recurring campaigns';

    if (r.frequency === 'monthly' && !(r.dayOfMonth >= 1 && r.dayOfMonth <= 31)) {
      errors.dayOfMonth = 'Day of month must be between 1 and 31';
    }
    if (r.frequency === 'annually' && !(r.dayOfYear >= 1 && r.dayOfYear <= 31)) {
      errors.dayOfYear = 'Day must be between 1 and 31';
    }

    if (r.endType === 'date') {
      if (!r.endDate) {
        errors.recurringEndDate = 'End date is required';
      } else if (r.startDate && new Date(r.endDate) < new Date(r.startDate)) {
        errors.recurringEndDate = 'End date cannot be before the start date';
      } else if (new Date(`${r.endDate}T23:59`) < now) {
        errors.recurringEndDate = 'End date must be today or in the future';
      }
    } else if (!(r.numberOfCycles >= 1)) {
      errors.numberOfCycles = 'Number of cycles must be at least 1';
    }
  }
  return errors;
};

function PersonalisationScheduling({ value, onChange, errors = {}, idPrefix }) {
  const setField = (field, fieldValue) => onChange({ ...value, [field]: fieldValue });
  const setRecurring = (field, fieldValue) =>
    onChange({ ...value, recurring: { ...value.recurring, [field]: fieldValue } });

  const fieldClass = (name) => (errors[name] ? 'input-error' : '');
  const fieldError = (name) => errors[name] && <span className="field-error-message">{errors[name]}</span>;
  const id = (name) => `${idPrefix}-${name}`;

  return (
    <div className="form-section">
      <h4>Scheduling</h4>

      <div className="form-group">
        <label>Campaign Type *</label>
        <div className="scheduling-type-tabs">
          <button
            type="button"
            className={`scheduling-tab ${value.schedulingType === 'one-time' ? 'active' : ''}`}
            onClick={() => setField('schedulingType', 'one-time')}
          >
            One-Time Campaign
          </button>
          <button
            type="button"
            className={`scheduling-tab ${value.schedulingType === 'recurring' ? 'active' : ''}`}
            onClick={() => setField('schedulingType', 'recurring')}
          >
            Recurring Campaign
          </button>
        </div>
      </div>

      {value.schedulingType === 'one-time' ? (
        <div className="one-time-scheduling">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor={id('scheduledDate')}>
                Scheduled Date<span className="required-asterisk">*</span>
              </label>
              <input
                type="date"
                id={id('scheduledDate')}
                value={value.scheduledDate}
                onChange={(e) => setField('scheduledDate', e.target.value)}
                min={today()}
                className={fieldClass('scheduledDate')}
              />
              {fieldError('scheduledDate')}
            </div>

            <div className="form-group">
              <label htmlFor={id('scheduledTime')}>
                Scheduled Time<span className="required-asterisk">*</span>
              </label>
              <input
                type="time"
                id={id('scheduledTime')}
                value={value.scheduledTime}
                onChange={(e) => setField('scheduledTime', e.target.value)}
                className={fieldClass('scheduledTime')}
              />
              {fieldError('scheduledTime')}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor={id('scheduledEndDate')}>
                Scheduled End Date<span className="required-asterisk">*</span>
              </label>
              <input
                type="date"
                id={id('scheduledEndDate')}
                value={value.scheduledEndDate}
                onChange={(e) => setField('scheduledEndDate', e.target.value)}
                min={value.scheduledDate || today()}
                className={fieldClass('scheduledEndDate')}
              />
              {fieldError('scheduledEndDate')}
            </div>

            <div className="form-group">
              <label htmlFor={id('scheduledEndTime')}>
                Scheduled End Time<span className="required-asterisk">*</span>
              </label>
              <input
                type="time"
                id={id('scheduledEndTime')}
                value={value.scheduledEndTime}
                onChange={(e) => setField('scheduledEndTime', e.target.value)}
                className={fieldClass('scheduledEndTime')}
              />
              {fieldError('scheduledEndTime')}
            </div>
          </div>

          {errors.scheduledEndDateTime && (
            <div className="error-message">{errors.scheduledEndDateTime}</div>
          )}
        </div>
      ) : (
        <div className="recurring-scheduling">
          <div className="form-group">
            <label htmlFor={id('frequency')}>Frequency *</label>
            <select
              id={id('frequency')}
              value={value.recurring.frequency}
              onChange={(e) => setRecurring('frequency', e.target.value)}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="annually">Annually</option>
            </select>
          </div>

          {value.recurring.frequency === 'weekly' && (
            <div className="form-group">
              <label htmlFor={id('dayOfWeek')}>Day of Week *</label>
              <select
                id={id('dayOfWeek')}
                value={value.recurring.dayOfWeek}
                onChange={(e) => setRecurring('dayOfWeek', e.target.value)}
              >
                <option value="monday">Monday</option>
                <option value="tuesday">Tuesday</option>
                <option value="wednesday">Wednesday</option>
                <option value="thursday">Thursday</option>
                <option value="friday">Friday</option>
                <option value="saturday">Saturday</option>
                <option value="sunday">Sunday</option>
              </select>
            </div>
          )}

          {value.recurring.frequency === 'monthly' && (
            <div className="form-group">
              <label htmlFor={id('dayOfMonth')}>Day of Month *</label>
              <input
                type="number"
                id={id('dayOfMonth')}
                min="1"
                max="31"
                value={value.recurring.dayOfMonth}
                onChange={(e) => setRecurring('dayOfMonth', parseInt(e.target.value, 10) || 0)}
                className={fieldClass('dayOfMonth')}
              />
              {fieldError('dayOfMonth')}
              <div className="form-helper-text">Enter a number between 1-31</div>
            </div>
          )}

          {value.recurring.frequency === 'annually' && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor={id('monthOfYear')}>Month *</label>
                <select
                  id={id('monthOfYear')}
                  value={value.recurring.monthOfYear}
                  onChange={(e) => setRecurring('monthOfYear', e.target.value)}
                >
                  {['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august',
                    'september', 'october', 'november', 'december'].map(month => (
                    <option key={month} value={month}>{month.charAt(0).toUpperCase() + month.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor={id('dayOfYear')}>Day *</label>
                <input
                  type="number"
                  id={id('dayOfYear')}
                  min="1"
                  max="31"
                  value={value.recurring.dayOfYear}
                  onChange={(e) => setRecurring('dayOfYear', parseInt(e.target.value, 10) || 0)}
                  className={fieldClass('dayOfYear')}
                />
                {fieldError('dayOfYear')}
                <div className="form-helper-text">Day of the month (1-31)</div>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor={id('recurringTime')}>Time *</label>
              <input
                type="time"
                id={id('recurringTime')}
                value={value.scheduledTime}
                onChange={(e) => setField('scheduledTime', e.target.value)}
                className={fieldClass('scheduledTime')}
              />
              {fieldError('scheduledTime')}
              <div className="form-helper-text">Time each run starts showing</div>
            </div>
            <div className="form-group">
              <label htmlFor={id('recurringEndTime')}>End Time *</label>
              <input
                type="time"
                id={id('recurringEndTime')}
                value={value.scheduledEndTime}
                onChange={(e) => setField('scheduledEndTime', e.target.value)}
                className={fieldClass('scheduledEndTime')}
              />
              {fieldError('scheduledEndTime')}
              <div className="form-helper-text">Time each run stops (an earlier time than the start ends the next day)</div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor={id('recurringStartDate')}>Start Date *</label>
              <input
                type="date"
                id={id('recurringStartDate')}
                value={value.recurring.startDate}
                onChange={(e) => setRecurring('startDate', e.target.value)}
                min={today()}
                className={fieldClass('recurringStartDate')}
              />
              {fieldError('recurringStartDate')}
            </div>
          </div>

          <div className="form-group">
            <label>Campaign End *</label>
            <div className="recurring-end-type">
              <label className="radio-label">
                <input
                  type="radio"
                  name={id('endType')}
                  value="date"
                  checked={value.recurring.endType === 'date'}
                  onChange={(e) => setRecurring('endType', e.target.value)}
                />
                <span>End on specific date</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name={id('endType')}
                  value="cycles"
                  checked={value.recurring.endType === 'cycles'}
                  onChange={(e) => setRecurring('endType', e.target.value)}
                />
                <span>End after number of cycles</span>
              </label>
            </div>
          </div>

          {value.recurring.endType === 'date' ? (
            <div className="form-group">
              <label htmlFor={id('recurringEndDate')}>End Date *</label>
              <input
                type="date"
                id={id('recurringEndDate')}
                value={value.recurring.endDate}
                onChange={(e) => setRecurring('endDate', e.target.value)}
                min={value.recurring.startDate || today()}
                className={fieldClass('recurringEndDate')}
              />
              {fieldError('recurringEndDate')}
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor={id('numberOfCycles')}>Number of Cycles *</label>
              <input
                type="number"
                id={id('numberOfCycles')}
                min="1"
                value={value.recurring.numberOfCycles}
                onChange={(e) => setRecurring('numberOfCycles', parseInt(e.target.value, 10) || 0)}
                className={fieldClass('numberOfCycles')}
              />
              {fieldError('numberOfCycles')}
              <div className="form-helper-text">How many times should this campaign run?</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PersonalisationScheduling;
